// packages/shared-components/src/utils/color.ts

import {
  DEFAULT_PALETTE_COLORS,
  DEFAULT_PICKER_COLORS,
  EXTENDED_PICKER_COLORS,
} from "../config/color";

/**
 * 颜色工具函数
 * 提供颜色格式转换（供 Palette 和 ColorPicker 共享）
 */

/**
 * RGB 颜色值
 */
export interface RgbColor {
  /** 红色分量 (0-255) */
  r: number;
  /** 绿色分量 (0-255) */
  g: number;
  /** 蓝色分量 (0-255) */
  b: number;
}

/**
 * HSV 颜色值
 */
export interface HsvColor {
  /** 色相 (0-360°) */
  h: number;
  /** 饱和度 (0-100%) */
  s: number;
  /** 明度 (0-100%) */
  v: number;
}

/**
 * HEX 转 RGB
 * @param hex HEX 颜色值 (如 #FF5722)
 * @returns RGB 颜色对象
 *
 * @example
 * ```ts
 * hexToRgb('#FF5722') // { r: 255, g: 87, b: 34 }
 * ```
 */
export function hexToRgb(hex: string): RgbColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}

/**
 * RGB 转 HEX
 * @param r 红色分量 (0-255)
 * @param g 绿色分量 (0-255)
 * @param b 蓝色分量 (0-255)
 * @returns HEX 颜色值 (如 #ff5722)
 *
 * @example
 * ```ts
 * rgbToHex(255, 87, 34) // '#ff5722'
 * ```
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((x) => {
      const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    })
    .join("")}`;
}

/**
 * RGB 转 HSV
 * @param r 红色分量 (0-255)
 * @param g 绿色分量 (0-255)
 * @param b 蓝色分量 (0-255)
 * @returns HSV 颜色对象
 *
 * @example
 * ```ts
 * rgbToHsv(255, 87, 34) // { h: 14, s: 86.7, v: 100 }
 * ```
 */
export function rgbToHsv(r: number, g: number, b: number): HsvColor {
  r = r / 255;
  g = g / 255;
  b = b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  let h = 0;
  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: h * 360, // 色相 (0-360°)
    s: s * 100, // 饱和度 (0-100%)
    v: v * 100, // 明度 (0-100%)
  };
}

/**
 * HSV 转 RGB
 * @param h 色相 (0-360°)
 * @param s 饱和度 (0-100%)
 * @param v 明度 (0-100%)
 * @returns RGB 颜色对象
 *
 * @example
 * ```ts
 * hsvToRgb(14, 86.7, 100) // { r: 255, g: 87, b: 34 }
 * ```
 */
export function hsvToRgb(h: number, s: number, v: number): RgbColor {
  h = h / 360;
  s = s / 100;
  v = v / 100;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r = 0,
    g = 0,
    b = 0;

  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * HEX 转 RGBA
 * @param hex HEX 颜色值 (如 #FF5722)
 * @param alpha 透明度 (0-1)
 * @returns RGBA 颜色字符串 (如 rgba(255, 87, 34, 0.8))
 *
 * @example
 * ```ts
 * hexToRgba('#FF5722', 0.8) // 'rgba(255, 87, 34, 0.8)'
 * ```
 */
export function hexToRgba(hex: string, alpha: number = 1): string {
  const rgb = hexToRgb(hex);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * RGBA 转 HEX
 * @param rgba RGBA 颜色字符串 (如 rgba(255, 87, 34, 0.8))
 * @returns HEX 颜色值 (如 #ff5722)
 *
 * @example
 * ```ts
 * rgbaToHex('rgba(255, 87, 34, 0.8)') // '#ff5722'
 * ```
 */
export function rgbaToHex(rgba: string): string {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return "#000000";

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  return rgbToHex(r, g, b);
}

/**
 * 提取 RGBA 的 alpha 值
 * @param rgba RGBA 颜色字符串 (如 rgba(255, 87, 34, 0.8))
 * @returns 透明度值 (0-1)
 *
 * @example
 * ```ts
 * getAlphaFromRgba('rgba(255, 87, 34, 0.8)') // 0.8
 * ```
 */
export function getAlphaFromRgba(rgba: string): number {
  const match = rgba.match(/rgba?\(\d+,\s*\d+,\s*\d+(?:,\s*([\d.]+))?\)/);
  return match && match[1] ? parseFloat(match[1]) : 1;
}

/**
 * 获取默认预设颜色（用于 Palette 调色板）
 * @returns 预设颜色数组
 *
 * @example
 * ```ts
 * const colors = getDefaultPaletteColors()
 * // ['#FF5722', '#FF9800', ...]
 * ```
 */
export function getDefaultPaletteColors(): string[] {
  return DEFAULT_PALETTE_COLORS;
}

/**
 * 获取默认预设颜色（用于 ColorPicker 颜色选择器）
 * @param extended 是否返回扩展的颜色集（用于弹出面板）
 * @returns 预设颜色数组
 *
 * @example
 * ```ts
 * const colors = getDefaultPickerColors() // 基础 8 个颜色
 * const extendedColors = getDefaultPickerColors(true) // 扩展 80 个颜色
 * ```
 */
export function getDefaultPickerColors(extended: boolean = false): string[] {
  return extended ? EXTENDED_PICKER_COLORS : DEFAULT_PICKER_COLORS;
}

/**
 * 获取默认 RGBA 颜色数组
 * @param alpha 透明度 (0-1，默认 1)
 * @returns RGBA 颜色数组
 *
 * @example
 * ```ts
 * const colors = getDefaultRgbaColors(0.8)
 * // ['rgba(255, 87, 34, 0.8)', 'rgba(255, 152, 0, 0.8)', ...]
 * ```
 */
export function getDefaultRgbaColors(alpha: number = 1): string[] {
  return DEFAULT_PICKER_COLORS.map((hex) => hexToRgba(hex, alpha));
}
